import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Sidebar } from '@/components/setting/sidebar';
import { Button } from '@/components/ui/button';
import { App, InputNumber, Select, Spin } from 'antd';
import { usePmv, usePmvMetric } from '@/api/hooks/usePmv';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { patchPmv } from '@/api/pmv';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { AutomationList } from '@/pages/autopilot/automation-list';
import { Profiles } from '@/pages/autopilot/profiles';
import { Calendar } from '@/pages/autopilot/calendar';
import { Blueprints } from '@/pages/autopilot/blueprints';
import { UserManagement } from '@/components/users/UserManagement';

interface ParameterInputProps {
  label?: string;
  value?: number;
  defaultValue?: number;
  unit?: string;
  operationalRange?: string;
  options?: Array<{ label: string; value: number; metric_value: number }>;
  onChange: (value: number, item?: { metric_value: number }) => void;
}

interface PmvVarInterface {
  metabolic_rate: number;
  clothing_insulation: number;
  air_speed: number;
  mrt_offset: number;
}

function ParameterInput({ label, value, unit, operationalRange, options, defaultValue, onChange }: ParameterInputProps) {
  return (
    <div className="space-y-1">
      <div className="flex flex-col">
        <div className="paragraph-10 text-bleck">{label}</div>
        {operationalRange && (
          <div className="paragraph-9 leading-[15px] flex items-center text-dark-grey">
            {operationalRange}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          {options ? (
            <div className="relative">
              <Select
                defaultValue={defaultValue}
                className="w-full h-8"
                onChange={(value, option) => {
                  const selectedOption = Array.isArray(option) ? option[0] : option;
                  onChange(Number(value), selectedOption as { metric_value: number });
                }}
                options={options}
              />
            </div>
          ) : (
            <InputNumber
              value={value}
              step={0.1}
              onChange={(val) => onChange(Number(val))}
              className="w-full h-8 bg-white border border-light-grey rounded-lg focus:border-primary focus:outline-none"
            />
          )}
        </div>
        {unit && (
          <div className="w-16 h-8 flex items-center justify-center text-xs text-dark-grey bg-white border border-light-grey rounded-lg">
            {unit}
          </div>
        )}
      </div>
    </div>
  );
}

interface ReferenceValueProps {
  title: string;
  values: Array<{ label: string; value: string }>;
}

function ReferenceValue({ title, values }: ReferenceValueProps) {
  return (
    <div className="p-1.5 border border-white-blue rounded-[5px]">
      <div className="paragraph-10">{title}</div>
      <p className="space-y-0.5 paragraph-10 leading-[15px] pl-1">
        {values.map((item, index) => (
          <React.Fragment key={index}>
            {`• ${item.label}: ${item.value}`}
            <br />
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

function EnvironmentSettings() {
  const [pmvVar, setPmvVar] = useState<PmvVarInterface | undefined>();
  const { data: pmvData, isLoading: pmvLoading, refetch } = usePmv();
  const { data: clothingData, isLoading: clothingLoading } = usePmvMetric({ variable: 'clothing_insulation' });
  const { data: metabolicRateData, isLoading: metabolicRateLoading } = usePmvMetric({ variable: 'metabolic_rate' });
  const { message } = App.useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!pmvData) return;
    setPmvVar({
      metabolic_rate: pmvData.metabolic_rate,
      clothing_insulation: pmvData.clothing_insulation,
      air_speed: pmvData.air_speed,
      mrt_offset: pmvData.mrt_offset,
    });
  }, [pmvData]);

  useEffect(() => {
    refetch();
  }, [navigate, refetch]);

  const onSave = async () => {
    if (!pmvVar) return;

    try {
      await patchPmv(pmvVar);
      message.success(SUCCESS_MESSAGE());
      refetch();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  if (pmvLoading || clothingLoading || metabolicRateLoading || !pmvData || !pmvVar) {
    return (
      <div className="flex-1 space-y-4 overflow-auto">
        <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
        <div className="bg-white rounded-lg shadow-card border border-light-grey p-2.5 space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-5 w-64 bg-gray-200 rounded animate-pulse" />
            <div className="h-9 w-16 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-full bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
          <div className="border-t border-light-grey my-4" />
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-full bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-3 w-36 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
          <div className="p-2.5 bg-bg rounded-lg space-y-2">
            <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="grid grid-cols-4 gap-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-1.5 border border-white-blue rounded-[5px] space-y-2">
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getDefaultMetabolicRate = () => {
    if (!metabolicRateData) return undefined;
    const defaultItem = metabolicRateData.find(v => v.default);
    return defaultItem?.id;
  };

  const getDefaultClothing = () => {
    if (!clothingData) return undefined;
    const defaultItem = clothingData.find(v => v.default);
    return defaultItem?.id;
  };

  return (
    <div className="flex-1 space-y-4 overflow-auto">
      <p className="heading-6 text-dark-blue">Environment</p>
      <div className="flex flex-col bg-white rounded-lg shadow-card gap-4 p-2.5 border border-light-grey">
        <div className="flex justify-between items-center">
          <h2 className="paragraph-14 font-semibold text-dark-blue">Predicted Mean Vote (PMV) Parameters</h2>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="md"
              className="bg-primary text-white"
              onClick={onSave}
              disabled={
                pmvVar?.metabolic_rate === pmvData.metabolic_rate &&
                pmvVar?.clothing_insulation === pmvData.clothing_insulation &&
                pmvVar?.air_speed === pmvData.air_speed &&
                pmvVar?.mrt_offset === pmvData.mrt_offset
              }
            >
              <p className="button-s">Save</p>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ParameterInput
            value={pmvVar.metabolic_rate}
            onChange={(value) => {
              setPmvVar({
                ...pmvVar,
                metabolic_rate: value,
              });
            }}
            label="Metabolic Rate"
            unit="met"
          />
          <ParameterInput
            operationalRange="Operational range: 0.7-2.1 met"
            defaultValue={getDefaultMetabolicRate()}
            onChange={(_, item) => {
              if (item) {
                setPmvVar({
                  ...pmvVar,
                  metabolic_rate: item.metric_value,
                });
              }
            }}
            options={metabolicRateData?.map(v => ({
              label: `${v.item_name}: ${v.metric_value}`,
              value: v.id,
              metric_value: v.metric_value,
            }))}
          />
          <ParameterInput
            label="Clothing Insulation"
            value={pmvVar.clothing_insulation}
            onChange={(value) => {
              setPmvVar({
                ...pmvVar,
                clothing_insulation: value,
              });
            }}
            unit="clo"
          />
          <ParameterInput
            operationalRange="Operational range: 0.36-1.0 clo"
            defaultValue={getDefaultClothing()}
            onChange={(_, item) => {
              if (item) {
                setPmvVar({
                  ...pmvVar,
                  clothing_insulation: item.metric_value,
                });
              }
            }}
            options={clothingData?.map(v => ({
              label: `${v.item_name}: ${v.metric_value}`,
              value: v.id,
              metric_value: v.metric_value,
            }))}
          />
        </div>

        <div className="border-t border-light-grey my-4" />

        <div className="grid grid-cols-2 gap-4">
          <ParameterInput
            label="Air Speed"
            value={pmvVar.air_speed}
            onChange={(value) => {
              setPmvVar({
                ...pmvVar,
                air_speed: value,
              });
            }}
            unit="m/s"
            operationalRange="Operational range: 0-1.0 m/s"
          />
          <ParameterInput
            label="Mean Radiant Temperature offset"
            value={pmvVar.mrt_offset}
            onChange={(value) => {
              setPmvVar({
                ...pmvVar,
                mrt_offset: value,
              });
            }}
            unit="°C"
            operationalRange="Recommended: < 4°C"
          />
        </div>

        <div className="flex flex-col p-2.5 gap-2.5 bg-bg rounded-lg">
          <div className="flex items-center gap-2.5">
            <div className="heading-7 text-dark-blue">
              Parameter Reference Values
            </div>
            <button className="flex items-center gap-1 paragraph-10 text-primary">
              Learn more
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            <ReferenceValue
              title="Metabolic Rate Values (met units):"
              values={[
                { label: "Resting", value: "1.0 met" },
                { label: "Sedentary activity", value: "1.2 met" },
                { label: "Light activity", value: "1.6 met" },
                { label: "Medium activity", value: "2.0 met" },
              ]}
            />
            <ReferenceValue
              title="Clothing Insulation Values (clo units):"
              values={[
                { label: "Light ensemble", value: "0.5 clo" },
                { label: "Medium ensemble", value: "1.0 clo" },
                { label: "Heavy ensemble", value: "1.5 clo" },
              ]}
            />
            <ReferenceValue
              title="Mean Radiant Temperature Offset:"
              values={[
                { label: "Proximity to solar radiation", value: "+2.0 to +5.0°C" },
                { label: "Proximity to thermal sources", value: "+1.0 to +3.0°C" },
                { label: "Well-insulated environments", value: "0.0°C" },
              ]}
            />
            <ReferenceValue
              title="Air Velocity Values (m/s):"
              values={[
                { label: "Still air conditions", value: "0.05-0.15 m/s" },
                { label: "Standard ventilation", value: "0.15-0.25 m/s" },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  return (
    <div className="flex gap-4 p-4 h-full overflow-hidden">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<UserManagement />} />
          <Route path="/user-management" element={<UserManagement />} />
          <Route path="/environment" element={<EnvironmentSettings />} />
          <Route path="/automations" element={<AutomationList />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/blueprints" element={<Blueprints />} />
        </Routes>
      </div>
    </div>
  );
}